import * as React from "react";
import {
  useState,
  useEffect,
  useRef,
  useContext,
  type ChangeEvent,
  type MouseEvent,
  type CSSProperties,
} from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Button,
  Box,
  Dialog,
  IconButton,
  TextField,
  Divider,
} from "@mui/material";
import { Close as CloseIcon, Save as SaveIcon } from "@mui/icons-material";
import SnackbarCustom, {
  type SnackbarData,
} from "../../components/SnackBarCustom";
import PopoverCustom from "../../components/PopoverCustom";
import { formatDate } from "../../utils/deteUtils";
import { DisasterRole } from "../../role";
import { TokenContext } from "../../TokenContext";
import DatePickerJapanese from "../../components/DatePickerJapanese";
import { getDisasters, updateDisaster } from "../../api";
import type {
  DisasterItem,
  DisasterKind,
  GetDisastersParams,
} from "../../types/disaster";
import type { IdParam } from "../../types/common";

type DisasterSearchContent = Pick<
  GetDisastersParams,
  "name" | "start_at_stt" | "start_at_end"
>;

type SsLogDisasterItem = DisasterItem & {
  complete_user?: { name?: string };
  update_at?: string;
  complete_at?: string | null;
};

type DisasterColumnId =
  | "create_at"
  | "update_at"
  | "kind"
  | "name"
  | "disaster_status_count"
  | "complete_at"
  | "user_name"
  | "buttonSpace";

type DisasterColumn = {
  id: DisasterColumnId;
  label: string;
  minWidth: string;
  align?: "left" | "right" | "center";
  kind?: boolean;
  textField?: boolean;
  isSmallScreen: boolean;
  complete_user?: boolean;
  buttons?: boolean;
  position?: CSSProperties["position"];
  right?: number;
  backgroundColor?: string;
  zIndex?: number;
  format?: (value: number) => string;
};

const columns: DisasterColumn[] = [
  {
    id: "create_at",
    label: "災害対応日時（始）",
    minWidth: "144px",
    isSmallScreen: false,
  },
  {
    id: "update_at",
    label: "災害対応日時（至）",
    minWidth: "144px",
    isSmallScreen: false,
  },
  {
    id: "kind",
    label: "災害種別",
    minWidth: "96px",
    align: "left",
    kind: true,
    isSmallScreen: true,
  },
  {
    id: "name",
    label: "災害名",
    minWidth: "184px",
    align: "left",
    format: (value) => value.toLocaleString("en-US"),
    textField: true,
    isSmallScreen: true,
  },
  {
    id: "disaster_status_count",
    label: "災害SS数",
    minWidth: "80px",
    align: "left",
    format: (value) => value.toFixed(2),
    isSmallScreen: false,
  },
  {
    id: "complete_at",
    label: "完了日",
    minWidth: "144px",
    align: "left",
    format: (value) => value.toFixed(2),
    isSmallScreen: false,
  },
  {
    id: "user_name",
    label: "完了実施者",
    minWidth: "152px",
    align: "left",
    format: (value) => value.toFixed(2),
    isSmallScreen: false,
    complete_user: true,
  },
  {
    id: "buttonSpace",
    label: "",
    minWidth: "160px",
    align: "right",
    format: (value) => value.toFixed(2),
    buttons: true,
    isSmallScreen: false,
    position: "sticky",
    right: 0,
    backgroundColor: "#fff",
    zIndex: 1,
  },
];

type DisasterTextfieldProps = {
  disasterData: SsLogDisasterItem;
  changedValue: (value: string) => void;
  readOnly?: boolean;
  id?: number;
};

const DisasterTextfield = ({
  disasterData,
  changedValue,
  readOnly,
}: DisasterTextfieldProps) => {
  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    if (typeof changedValue === "function") {
      changedValue(e.target.value);
    }
  };

  return (
    <Box sx={{ display: "flex", gap: "8px" }}>
      <TextField
        name="disasterName"
        size="small"
        variant="outlined"
        value={disasterData?.name}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => handleChange(e)}
        sx={{ pointerEvents: "auto" }}
        InputProps={{
          readOnly: readOnly, // ここでreadOnlyを設定
        }}
      />
    </Box>
  );
};

const SearchResult = () => {
  const [cleared, setCleared] = React.useState(false);

  React.useEffect(() => {
    if (cleared) {
      const timeout = setTimeout(() => {
        setCleared(false);
      }, 1500);

      return () => clearTimeout(timeout);
    }
    return () => { };
  }, [cleared]);
  //ページネーション管理
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  //検索条件
  const [searchContent, setSearchContent] = useState<DisasterSearchContent>({});

  const [disasters, setDisasters] = useState<SsLogDisasterItem[]>([]);
  const [totalCount, setTotalCount] = useState<number | undefined>();

  const lastSearchedContentRef = useRef<DisasterSearchContent | null>(null);

  const getDisasterData = async (overridePage?: number) => {
    const pageToUse = overridePage !== undefined ? overridePage : page;
    // 検索ボタン押下時は現在の検索条件、ページネーション時は最後に確定した条件を使用
    const contentToUse =
      overridePage !== undefined
        ? searchContent
        : (lastSearchedContentRef.current ?? searchContent);
    try {
      const response = await getDisasters({
        name: contentToUse.name,
        is_completed: true,
        exclude_id: null,
        start_at_stt: contentToUse.start_at_stt,
        start_at_end: contentToUse.start_at_end,
        order: null,
        sort: null,
        page: pageToUse + 1,
        limit: rowsPerPage,
      });
      if (response.status !== "ok") {
        return;
      }
      const data = response.disasters;
      const total = response.total_count ?? 0;
      const status: SnackbarData = response;
      console.log("取得データ:", data, "入力データ:", contentToUse);
      setDisasters(data as SsLogDisasterItem[]);
      setTotalCount(total);
      if (overridePage !== undefined) {
        setPage(overridePage);
        lastSearchedContentRef.current = searchContent;
      } else {
        lastSearchedContentRef.current = contentToUse;
      }
      setSnackBarstatus(status);
      setSnackbarOpen(true);
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //初回起動時の読み込み回避
  const isFirstRender = useRef(false);

  useEffect(() => {
    // このeffectは初回レンダー時のみ呼ばれるeffect
    isFirstRender.current = true;
  }, []);

  useEffect(() => {
    if (isFirstRender.current) {
      // 初回レンダー判定
      isFirstRender.current = false; // もう初回レンダーじゃないよ代入
    } else {
      getDisasterData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowsPerPage, page]);

  const handleChangeTextField = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const newValue = e.target.value;
    setSearchContent((prevValue) => ({
      ...prevValue,
      name: newValue,
    }));
  };

  const handleChangeCalender = (e: Date | null, column?: string) => {
    if (e === null) {
      setSearchContent((prevValue) => ({
        ...prevValue,
        [column as keyof DisasterSearchContent]: null,
      }));
      return;
    }
    const year = e.getFullYear();
    const month = (e.getMonth() + 1).toString().padStart(2, "0");
    const day = e.getDate().toString().padStart(2, "0");
    const formattedDateStr = year + "/" + month + "/" + day;
    console.log("eventlog", formattedDateStr);
    setSearchContent((prevValue) => ({
      ...prevValue,
      [column as keyof DisasterSearchContent]: formattedDateStr,
    }));
  };

  const [selectedRow, setSelectedRow] = useState<SsLogDisasterItem | null>(null);
  //*ポップアップ設定 */
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handleLocationCodeClick = (row: SsLogDisasterItem, rowNum: number) => {
    setSelectedRow(row);
    setSelectedRowNum(rowNum);
    setIsPopoverOpen(true);
  };

  const handlePopoverClose = () => {
    setIsPopoverOpen(false);
  };

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  //スナックバー管理
  const [snackBarstatus, setSnackBarstatus] = useState<SnackbarData | undefined>();
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  //完了解除ポップオーバー管理
  const [confirmPopoverOpen, setConfirmPopoverOpen] = useState(false);
  const incompleteButtonClick = (e: MouseEvent, disasterId: number) => {
    e.stopPropagation();
    //選択した災害のIDをセット
    setPutDisasterId(disasterId);
    setConfirmPopoverOpen(true);
  };
  const confirmPopupClose = () => {
    setConfirmPopoverOpen(false);
  };

  const [putDisasterId, setPutDisasterId] = useState<IdParam | undefined>();
  const putInComplete = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    if (!putDisasterId) {
      return;
    }

    const disasterId = Number(putDisasterId);
    const params = {
      id: disasterId,
      is_complete: false,
    };
    console.log("完了データ", params);
    try {
      const status = await updateDisaster(putDisasterId, params);
      console.log("完了処理", status);
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      getDisasterData();
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //Excelダウンロード処理
  // const getExcelFile = async (e, disasterId) => {
  //   e.stopPropagation();
  //   const tokenInfo = await fetchSession();
  //   const apiUrl = `${process.env.REACT_APP_BASE_URL}/disaster-excel/${disasterId}`;
  //   // リクエストパラメータをオブジェクトとして定義
  //   const params = {
  //     params: { is_meti: true },
  //     headers: {
  //       Authorization: `Bearer ${tokenInfo.accessToken}`,
  //     },
  //   };

  //   // axiosを使用してGETリクエストを送信
  //   console.log(
  //     "Excelファイル取得入力データ",
  //     "disaster_id=" + disasterId,
  //     params
  //   );
  //   axios
  //     .get(apiUrl, params)
  //     .then((response) => {
  //       // レスポンスのデータを取得
  //       const status = response.data;
  //       console.log("Excelファイル取得", response);
  //       // テナント情報をステートにセット
  //       setSnackBarstatus(status);
  //       setSnackbarOpen(true);
  //     })
  //     .catch((error) => {
  //       console.error("API呼び出しエラー", error);
  //     });
  // };

  type ButtonValidateProps = { disasterId: number };

  const ButtonValidate = ({ disasterId }: ButtonValidateProps) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "8px",
          alignItems: "center",
        }}
      >
        <Box
          sx={{ display: "flex", flexDirection: "row", alignItems: "center" }}
        >
          <Button
            name="incompleteButton"
            variant="contained"
            onClick={(e) => incompleteButtonClick(e, disasterId)}
            sx={{
              marginLeft: "8px",
              pointerEvents: "auto",
              whiteSpace: "nowrap",
            }}
          >
            完了解除
          </Button>
        </Box>
      </Box>
    );
  };

  const tokenContext = useContext(TokenContext);
  const disasterRole = DisasterRole(
    tokenContext?.user?.m_organization.m_role_id ?? 0
  );
  const disasterValidate = (disasterName?: DisasterKind) => {
    return (
      <Box
        sx={{
          borderRadius: "20px",
          padding: "8px",
          color: "white",
          fontWeight: "bold",
          width: "fit-content",
          backgroundColor:
            disasterName === "quake"
              ? "red"
              : disasterName === "w-r-s"
                ? "orange"
                : disasterName === "tsunami"
                  ? "blue"
                  : disasterName === "manual"
                    ? "gray"
                    : "black",
        }}
      >
        {disasterName === "quake"
          ? "地震"
          : disasterName === "w-r-s"
            ? "雨風雪"
            : disasterName === "tsunami"
              ? "津波"
              : disasterName === "manual"
                ? "手動"
                : disasterName === "alert"
                  ? "警報"
                  : "その他"}
      </Box>
    );
  };

  //登録ボタン選択行番号管理
  const [selectedRowNum, setSelectedRowNum] = useState<number>(0);

  const handleChangeDisaster = (inputValue: string, siteId: number) => {
    console.log("inputValue, rowNum", inputValue, siteId);
    const updatedArray = [...disasters]; // オリジナルの配列をコピー
    //sites配列内からidが当てはまる配列を探す
    const updatedItem = updatedArray.find((item) => item.id === siteId);
    if (updatedItem) {
      updatedItem.name = inputValue;
      setDisasters(updatedArray);
    } else {
      console.error(`sites with id ${siteId} not found`);
    }
  };

  //災害名登録ポップオーバー管理
  const [putNamePopoverOpen, setPutNamePopoverOpen] = useState(false);
  const putNameButtonClick = (e: MouseEvent, rowNum: number) => {
    e.stopPropagation();
    setSelectedRowNum(rowNum);
    setPutNamePopoverOpen(true);
  };
  const putNamePopupClose = () => {
    setPutNamePopoverOpen(false);
  };

  //災害名更新
  const putDisasterName = async (isSuccess: boolean) => {
    if (!isSuccess) {
      console.log("何も行いません");
      return;
    }

    const params = {
      id: disasters[selectedRowNum].id,
      name: disasters[selectedRowNum].name,
      is_complete: true,
    };
    console.log("災害情報更新入力データ", params);
    try {
      const status = await updateDisaster(disasters[selectedRowNum].id, params);
      console.log("災害情報更新", status);
      //スナックバーステート
      setSnackBarstatus(status);
      setSnackbarOpen(true);
      getDisasterData();
    } catch (error) {
      console.error("API呼び出しエラー", error);
    }
  };

  //災害名更新ボタン
  type PutButtonProps = { rowNum: number };

  const PutButton = ({ rowNum }: PutButtonProps) => {
    return (
      <IconButton
        name="putButton"
        color="primary"
        sx={{ pointerEvents: "auto" }}
        onClick={(e) => putNameButtonClick(e, rowNum)}
      >
        <SaveIcon />
      </IconButton>
    );
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "fit-content",
        justifyContent: "center",
        borderRadius: 2,
        borderColor: "primary.main",
        boxShadow: 3,
        color: "outline.dark",
        backgroundColor: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "fit-content",
          justifyContent: "center",
          gap: "16px",
          paddingTop: "32px",
          paddingRight: "40px",
          paddingLeft: "40px",
          paddingBottom: "32px",
          "@media screen and (max-width:900px)": {
            padding: "16px",
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            paddingLeft: "40px",
            "@media screen and (max-width:900px)": {
              padding: "0",
            },
          }}
        >
          検索条件
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:600px)": {
              width: "100%",
              flexDirection: "column",
            },
          }}
        >
          <Box sx={{ width: "248px" }}>災害名称</Box>
          <TextField
            id="outlined-basic"
            variant="outlined"
            size="small"
            onChange={(e) => handleChangeTextField(e)}
            sx={{
              width: "372px",
              "@media screen and (max-width:900px)": {
                width: "248px",
              },
            }}
          />
        </Box>
        <Box
          sx={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "center",
            minWidth: "min-content",
            fontSize: 16,
            fontWeight: "bold",
            color: "#000",
            whiteSpace: "nowrap",
            "@media screen and (max-width:600px)": {
              flexDirection: "column",
              width: "100%",
              alignItems: "center",
            },
          }}
        >
          <Box sx={{ padding: "8px 0 0 0", width: "248px" }}>
            災害発生年月日
          </Box>
          <Box
            sx={{
              display: "flex",
              width: "372px",
              gap: "8px",
              "@media screen and (max-width:900px)": {
                width: "248px",
                flexDirection: "column",
              },
            }}
          >
            <DatePickerJapanese
              label="start"
              inputFormat="yyyy-MM-dd"
              placeholder="****年**月**日"
              onChange={handleChangeCalender}
              column="start_at_stt"
            />
            <DatePickerJapanese
              label="end"
              inputFormat="yyyy-MM-dd"
              placeholder="****年**月**日"
              onChange={handleChangeCalender}
              column="start_at_end"
            />
          </Box>
        </Box>
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "16px",
            "@media screen and (max-width:600px)": {
              flexDirection: "column",
              alignItems: "center",
            },
          }}
        >
          <Button
            name="searchButton"
            variant="contained"
            onClick={() => {
              setPage(0);
              getDisasterData(0);
            }}
            sx={{ width: "208px" }}
          >
            検索
          </Button>
        </Box>
      </Box>
      <Divider />
      <Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            height: "fit-content",
            justifyContent: "center",
            paddingTop: "32px",
            paddingRight: "40px",
            paddingLeft: "40px",
            paddingBottom: "32px",
            gap: "16px",
            "@media screen and (max-width:900px)": {
              padding: "16px",
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              minWidth: "min-content",
              fontSize: 16,
              fontWeight: "bold",
              color: "#000",
              whiteSpace: "nowrap",
              paddingLeft: "40px",
              "@media screen and (max-width:900px)": {
                padding: "0",
              },
            }}
          >
            検索結果
          </Box>
        </Box>
        <TableContainer>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                {columns.map((column) => (
                  <TableCell
                    key={column.id}
                    align={column.align}
                    style={{
                      minWidth: column.minWidth,
                      position: column.position,
                      right: column.right,
                    }}
                    sx={{
                      color: "outline.dark",
                      fontWeight: "bold",
                      borderBottom: 2,
                      padding: "8px",
                      "@media screen and (max-width:900px)": {
                        display:
                          column.isSmallScreen === true ? "table-cell" : "none",
                      },
                    }}
                  >
                    {column.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {disasters?.map((disaster, rowNum) => {
                return (
                  <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={disaster.id}
                    onClick={() => handleLocationCodeClick(disaster, rowNum)}
                    sx={{
                      pointerEvents: "none",
                      "@media screen and (max-width:900px)": {
                        cursor: "pointer",
                        pointerEvents: "auto",
                      },
                    }}
                  >
                    {columns.map((column) => {
                      return (
                        <TableCell
                          key={column.id}
                          align={column.align}
                          style={{
                            minWidth: column.minWidth,
                            position: column.position,
                            right: column.right,
                            backgroundColor: column.backgroundColor,
                          }}
                          sx={{
                            padding: "8px",
                            "@media screen and (max-width:900px)": {
                              display:
                                column.isSmallScreen === true
                                  ? "table-cell"
                                  : "none",
                            },
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "32px",
                            }}
                          >
                            {column.complete_user ? (
                              <>{disaster?.complete_user?.name}</>
                            ) : column.kind ? (
                              disasterValidate(disaster["kind"])
                            ) : column.textField ? (
                              <Box
                                sx={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "8px",
                                }}
                              >
                                <DisasterTextfield
                                  disasterData={disaster}
                                  id={disaster["id"]}
                                  changedValue={(value) =>
                                    handleChangeDisaster(value, disaster.id)
                                  }
                                  readOnly={!disasterRole.update}
                                />
                                {disasterRole.update ? (
                                  <PutButton rowNum={rowNum} />
                                ) : (
                                  <></>
                                )}
                              </Box>
                            ) : column.buttons ? (
                              disasterRole.update ? (
                                <ButtonValidate disasterId={disaster["id"]} />
                              ) : (
                                <></>
                              )
                            ) : column.id === "create_at" ||
                              column.id === "update_at" ||
                              column.id === "complete_at" ? (
                              formatDate(String(disaster[column.id] ?? ""))
                            ) : (
                              <>{disaster[column["id"]]}</>
                            )}
                          </Box>
                        </TableCell>
                      );
                    })}
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
        <Dialog open={isPopoverOpen} onClose={handlePopoverClose}>
          <Box
            sx={{
              display: "flex",
              height: "56px",
              justifyContent: "space-between",
              alignItems: "center",
              padding: "8px",
              borderBottom: "2px solid #CCC",
              position: "sticky",
              top: 0,
              backgroundColor: "#fff",
              zIndex: 1,
            }}
          >
            <Box
              sx={{
                paddingLeft: "8px",
                fontWeight: "bold",
                fontSize: "20px",
              }}
            >
              被害履歴詳細
            </Box>
            <IconButton
              aria-label="close"
              onClick={handlePopoverClose}
              sx={{
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Table sx={{ WebkitWritingMode: "horizontal-tb" }}>
            <TableBody>
              {selectedRow &&
                columns.map((column) => {
                  return (
                    <TableRow role="checkbox" tabIndex={-1} key={column.id}>
                      <TableCell
                        align={column.align}
                        sx={{
                          color: "outline.dark",
                          fontWeight: "bold",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            WebkitWritingMode: "horizontal-tb",
                            height: "100%",
                          }}
                        >
                          {column.label}
                        </Box>
                      </TableCell>
                      <TableCell
                        align={column.align}
                        sx={{
                          writingMode: "horizontal-tb",
                          boxSizing: "border-box",
                          width: "calc((100vw - 64px) / 2)",
                          overflowWrap: "break-word",
                        }}
                      >
                        {column.complete_user ? (
                          <>{selectedRow.complete_user?.name}</>
                        ) : column.kind ? (
                          disasterValidate(selectedRow["kind"])
                        ) : column.textField ? (
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                            }}
                          >
                            <DisasterTextfield
                              disasterData={selectedRow}
                              id={selectedRow?.["id"]}
                              changedValue={(value) =>
                                handleChangeDisaster(value, selectedRow?.id)
                              }
                              readOnly={!disasterRole.update}
                            />
                            {disasterRole.update ? (
                              <PutButton rowNum={selectedRowNum} />
                            ) : (
                              <></>
                            )}
                          </Box>
                        ) : column.buttons ? (
                          disasterRole.update ? (
                            <ButtonValidate disasterId={selectedRow?.["id"]} />
                          ) : (
                            <></>
                          )
                        ) : column.id === "create_at" ||
                          column.id === "update_at" ||
                          column.id === "complete_at" ? (
                          formatDate(String(selectedRow?.[column.id] ?? ""))
                        ) : (
                          <>{selectedRow?.[column["id"]]}</>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
            </TableBody>
          </Table>
        </Dialog>
        <TablePagination
          rowsPerPageOptions={[10, 30, 50, 100]}
          component="div"
          count={totalCount ?? 0}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          sx={{
            ".MuiToolbar-root,.MuiInputBase-root,.MuiTablePagination-actions":
            {
              margin: "0 !important",
              padding: "0 !important",
            },
          }}
          labelRowsPerPage="表示件数:"
          labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count !== -1 ? count : `more than ${to}`}`}
        />
      </Box>
      <SnackbarCustom
        data={snackBarstatus}
        openStatus={snackbarOpen}
        onClose={() => setSnackbarOpen(false)}
      />
      <PopoverCustom
        open={confirmPopoverOpen}
        onClose={confirmPopupClose}
        yesNo={(e) => putInComplete(e)}
        title={"完了解除確認"}
        contents={"完了を解除してもよろしいですか？"}
      />
      <PopoverCustom
        open={putNamePopoverOpen}
        onClose={putNamePopupClose}
        yesNo={(e) => putDisasterName(e)}
        title={"災害名登録確認"}
        contents={"災害名を登録してもよろしいですか？"}
      />
    </Box>
  );
};
export default SearchResult;
